import {
  BusinessLiveAccessStatus,
  LIVE_ACCESS_ALLOWED_EXTENSIONS,
  LIVE_ACCESS_MAX_FILE_SIZE_BYTES,
} from '@/types';

// Live-access documents and templates are PDF-only. `accept` on the picker is
// only advisory, so this is the hard guard applied on selection.
export const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

// UAT (submission + default template) is Excel-only; every other live-access
// document stays PDF-only. Extensions come from the shared core map so this
// can never drift from what the server enforces in BusinessLiveAccessInteractor.
export const isAllowedLiveAccessFile = (
  documentType: string,
  file: File,
): boolean => {
  const allowed = LIVE_ACCESS_ALLOWED_EXTENSIONS[documentType] ?? ['pdf'];
  return allowed.some(
    (ext) =>
      file.type === MIME_TYPE_BY_EXTENSION[ext] ||
      new RegExp(`\\.${ext}$`, 'i').test(file.name),
  );
};

// "Up to 10MB" is only a label unless something also enforces it — this is
// that enforcement, from the same shared core constant as business-web.
export const isWithinLiveAccessMaxSize = (file: File): boolean =>
  file.size <= LIVE_ACCESS_MAX_FILE_SIZE_BYTES;

export const liveAccessDocLabel = (documentType: string): string => {
  const map: Record<string, string> = {
    UAT: 'Afriex Partner UAT Template',
    BUSINESS_AGREEMENT: 'Business Agreement',
    FINAL_AGREEMENT: 'Signed Final Agreement',
  };
  return map[documentType] ?? documentType;
};

// Badge colorScheme for a live-access status (mirrors rfiStatusColorScheme).
export const liveAccessStatusColorScheme = (
  status?: BusinessLiveAccessStatus,
): string => {
  switch (status) {
    case BusinessLiveAccessStatus.Approved:
      return 'successful';
    case BusinessLiveAccessStatus.Rejected:
      return 'failed';
    case BusinessLiveAccessStatus.Requested:
      return 'pending';
    default:
      return 'gray';
  }
};
